import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormConfirmDialog, FormDrawer } from '../../../../components/ui';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { CAMPAIGN_COPY } from '../../constants/campaignCopy';
import useWorkspaceCreateForm from '../../hooks/useWorkspaceCreateForm';
import { buildNewFormStudioRoute } from '../../utils/businessFunctionRoutes';
import BusinessServiceCreateForm from './BusinessServiceCreateForm';

export default function CreateBusinessServiceDrawer({
  open,
  onClose,
  accountId,
  organisationId,
  onCreated,
}) {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [createdProposalId, setCreatedProposalId] = useState('');
  const [confirmFormOpen, setConfirmFormOpen] = useState(false);

  const handleCreated = useCallback(async (created) => {
    await onCreated?.(created);
    const proposalId = created?.id ? String(created.id) : '';
    if (proposalId) {
      setCreatedProposalId(proposalId);
      setConfirmFormOpen(true);
    }
  }, [onCreated]);

  const formState = useWorkspaceCreateForm({
    open,
    accountId,
    organisationId,
    onCreated: handleCreated,
    onClose,
  });

  const {
    form,
    fieldErrors,
    isSubmitting,
    submitError,
    updateField,
    submit,
  } = formState;

  const copy = CAMPAIGN_COPY.create;

  const isDirty = useMemo(
    () => Boolean(
      String(form.name || '').trim()
      || String(form.tentativeBudget ?? '').trim()
      || String(form.costOfLeads ?? '').trim()
    ),
    [form.costOfLeads, form.name, form.tentativeBudget]
  );

  const handleSave = useCallback(async () => {
    const result = await submit();
    if (result.success) {
      showSnackBar(copy.success, 'success');
    } else if (result.message) {
      showSnackBar(result.message, 'error');
    }
  }, [copy.success, showSnackBar, submit]);

  const handleConfirmFormClose = useCallback(() => {
    setConfirmFormOpen(false);
    setCreatedProposalId('');
  }, []);

  const handleCreateForm = useCallback(() => {
    if (!createdProposalId) {
      handleConfirmFormClose();
      return;
    }

    navigate(buildNewFormStudioRoute(accountId, createdProposalId));
    handleConfirmFormClose();
  }, [accountId, createdProposalId, handleConfirmFormClose, navigate]);

  return (
    <>
      <FormDrawer
        open={open}
        onClose={onClose}
        title={copy.title}
        subtitle={copy.subtitle}
        submitLabel={copy.submit}
        cancelLabel={copy.cancel}
        onSubmit={handleSave}
        isSubmitting={isSubmitting}
        disableClose={isSubmitting}
        confirmBeforeClose={isDirty}
        confirmTitle={copy.discard.title}
        confirmMessage={copy.discard.message}
        confirmCancelLabel={copy.discard.keepEditing}
        confirmConfirmLabel={copy.discard.discard}
      >
        <BusinessServiceCreateForm
          form={form}
          fieldErrors={fieldErrors}
          submitError={submitError}
          onFieldChange={updateField}
        />
      </FormDrawer>

      <FormConfirmDialog
        open={confirmFormOpen}
        onConfirm={handleCreateForm}
        onCancel={handleConfirmFormClose}
        title={copy.confirmForm.title}
        message={copy.confirmForm.message}
        confirmLabel={copy.confirmForm.confirm}
        cancelLabel={copy.confirmForm.cancel}
      />
    </>
  );
}
